import axiosClient from './axios-client';
import { PagedListResponse, PaginationParams } from './common.dto';
import type { PaymentMetadata, PaymentTransaction } from './payment';
import { API_ENDPOINTS } from '@/constants';

/** Statuses available in the order status dropdown */
export enum ShippingType {
  PENDING = 'Pending',
  AWAITING_PAYMENT = 'Awaiting Payment',
  PROCESSING = 'Processing',
  PAID = 'Paid',
  SHIPPED = 'Shipped',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  CANCELLATION_REQUESTED = 'Cancellation Requested',
  RETURN_REQUEST = 'Return Request',
  REFUNDED = 'Refunded',
  PARTIAL_REFUND = 'Partial Refund',
  ACTION_REQUIRED = 'Action Required By Customer',
  REQUEST_DOCUMENT = 'Request Document',
  CONTACT_BUYER = 'Contact Buyer',
  DOUBLED_ORDER = 'Doubled Order',
  STRIPE_PENDING = 'Stripe Pending',
  PAYPAL_PENDING = 'Paypal Pending',
  NPA_PENDING = 'NPA Pending',
  STRIPE_COMPLETED = 'Stripe Completed',
  PAYPAL_COMPLETED = 'Paypal Completed',
  NPA_COMPLETED = 'NPA Completed',
  PAYMENT_ACTION_REQUEST = 'Payment Action Request',
}

export enum StatusOrders {
  PENDING = 'Pending',
  AWAITING_PAYMENT = 'Awaiting Payment',
  PROCESSING = 'Processing',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  CANCELLATION_REQUESTED = 'Cancellation Requested',
  ACTION_REQUIRED_BY_CUSTOMER = 'Action Required By Customer',
  REQUEST_DOCUMENT = 'Request Document',
  CONTACT_BUYER = 'Contact Buyer',
  DOUBLED_ORDER = 'Doubled Order',
  PAID = 'Paid',
  RETURN_REQUEST = 'Return Request',
  REFUNDED = 'Refunded',
  PARTIAL_REFUND = 'Partial Refund',
  SHIPPED = 'Shipped',
  STRIPE_PENDING = 'Stripe Pending',
  PAYPAL_PENDING = 'Paypal Pending',
  NPA_PENDING = 'NPA Pending',
  STRIPE_COMPLETED = 'Stripe Completed',
  PAYPAL_COMPLETED = 'Paypal Completed',
  NPA_COMPLETED = 'NPA Completed',
  PAYMENT_ACTION_REQUEST = 'Payment Action Request',
}

export enum FraudStatus {
  PENDING = 'pending',
  REVIEW = 'review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum FraudRiskLevel {
  GREEN = 'green',
  ORANGE = 'orange',
  RED = 'red',
}

export enum AvailabilityResult {
  ALL_AVAILABLE = 'all_available',
  SOME_AVAILABLE = 'some_available',
  NONE_AVAILABLE = 'none_available',
}

export interface OrderAddress {
  id: string;
  addressType: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string | null;
  company: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface OrderItem {
  id: string;
  orderId: string;
  productId: string | null;
  sku: string | null;
  productName: string;
  variantName: string | null;
  quantity: number;
  unitPrice: string;
  taxAmount: string;
  totalPrice: string;
  vendorId: string | null;
  vendorStatus: string;
  /** Warehouse packing-list verification — set via PATCH /warehouse/orders/:id/items */
  isVerified?: boolean;
  missedItemsQty?: number;
  metadata: Record<string, unknown> | null;
  productSnapshot?: {
    id: string;
    sku: string | null;
    name: string;
    price: string;
    images: string[];
    brandId: string;
    brandName: string;
    condition: string;
    partNumber: string;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AssignedSalesperson {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  country: string | null;
  state: string | null;
  address: string | null;
  zipCode: string | null;
  city: string | null;
  company: string | null;
  status: string;
  userRole: string;
  isSalesperson: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface ChildOrder {
  id: string;
  orderNumber: string;
  customerId: string;
  storefrontId: string | null;
  parentOrderId: string | null;
  marketplace: string | null;
  marketplaceOrderId: string | null;
  status: StatusOrders;
  paymentStatus: string;
  fraudStatus: string;
  fraudRiskLevel: string | null;
  warehouseStatus: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  shippingAmount: string;
  handlingAmount: string;
  insuranceAmount: string;
  feesAmount: string;
  totalAmount: string;
  /** Tax removed by staff (customer sent a tax certificate) */
  isTaxExempt?: boolean;
  taxExemptNote?: string | null;
  /** Tax amount was typed by staff and is no longer recalculated */
  isTaxManual?: boolean;
  shippingAddressId: string;
  billingAddressId: string;
  shippingMethod: string | null;
  trackingNumber: string | null;
  assignedSalespersonId: string | null;
  preferredContactMethod: string | null;
  metadata: PaymentMetadata;
  ipAddress: string | null;
  shippingAddress?: OrderAddress;
  billingAddress?: OrderAddress;
  items?: OrderItem[];
  transactions?: PaymentTransaction[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  ipAddress?: string | null;
  customer: {
    id: string;
    userId: string;
    email: string;
    fullName: string;
    phone: string | null;
    customerType: string;
    companyName: string | null;
    isBlacklisted: boolean;
    blacklistReason: string | null;
    assignedSalespersonId: string | null;
    lifetimeValue: string;
    totalOrders: number;
    status: string;
    ipAddress?: string | null;
    preferredContactMethod?: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
  storefrontId: string | null;
  storefront: { id: string; name: string; domain: string } | null;
  parentOrderId: string | null;
  marketplace: string | null;
  marketplaceOrderId: string | null;
  status: StatusOrders;
  paymentStatus: string;
  fraudStatus: string;
  fraudRiskLevel: string | null;
  warehouseStatus: string;
  /** Nullable — many orders predate this column and have never had it set */
  packageType: 'box' | 'pallet' | null;
  /** Nullable — many orders predate this column and have never had it set */
  orderClassification: 'domestic' | 'international' | null;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  shippingAmount: string;
  handlingAmount: string;
  insuranceAmount: string;
  feesAmount: string;
  totalAmount: string;
  /** Tax removed by staff (customer sent a tax certificate) */
  isTaxExempt?: boolean;
  taxExemptNote?: string | null;
  /** Tax amount was typed by staff and is no longer recalculated */
  isTaxManual?: boolean;
  shippingAddressId: string;
  billingAddressId: string;
  shippingAddress: OrderAddress;
  billingAddress: OrderAddress;
  shippingMethod: string | null;
  trackingNumber: string | null;
  assignedSalespersonId: string | null;
  assignedSalesperson: AssignedSalesperson | null;
  /** Populated on warehouse endpoints (GET /warehouse/orders/:id) — not loaded on the general Orders endpoints. */
  assignedWarehouseHandlerId?: string | null;
  assignedWarehouseHandler?: AssignedSalesperson | null;
  preferredContactMethod: string | null;
  metadata: PaymentMetadata;
  transactions: PaymentTransaction[];
  items: OrderItem[];
  childOrders: ChildOrder[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Alias for fe-copied components */
export type Orders = Order;

export interface OrderParams extends PaginationParams {
  id?: string;
  orderNumber?: string;
  status?: StatusOrders;
  paymentStatus?: string;
  customerId?: string;
  fraudStatus?: FraudStatus;
  fraudRiskLevel?: FraudRiskLevel;
  warehouseStatus?: string;
  search?: string;
}

export interface AssignSalespersonRequest {
  salespersonId: string;
}

export interface ItemsAvailabilityRequest {
  availabilityResult: AvailabilityResult;
  /** Required when availabilityResult is some_available */
  unavailableItemIds?: string[];
  note?: string;
}

export interface UpdateOrderStatusRequest {
  status: StatusOrders;
}

export interface UpdateOrderRequest {
  status?: StatusOrders;
  trackingNumber?: string;
  shippingMethod?: string;
  paymentStatus?: string;
  warehouseStatus?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateShippingAddressRequest {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  company?: string;
}

export interface CancelOrderRequest {
  reason?: string;
}

export interface ApprovePartialRequest {
  approved: boolean;
}

export interface RefundOrderRequest {
  amount?: number;
  reason?: string;
}

export type DocumentType =
  | 'invoice'
  | 'invoice_no_shipping'
  | 'credit_card_auth'
  | 'order_status_confirm';

export interface SendDocumentRequest {
  // Omitted/empty → backend falls back to its default document-request message
  message?: string;
}

// ─── Order relations ──────────────────────────────────────────────────────────

export interface OrderGroup {
  id: string;
  orders: Order[];
}

// ─── Order files ──────────────────────────────────────────────────────────────

export interface OrderFile {
  id: string;
  orderId: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  uploadedBy: string;
  createdAt: string;
}

// ─── Order notes ──────────────────────────────────────────────────────────────

export interface OrderNote {
  id: string;
  orderId: string;
  note: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddOrderNoteRequest {
  text: string;
}

// ─── Status logs ──────────────────────────────────────────────────────────────

export interface StatusLog {
  id: string;
  orderId: string;
  status: StatusOrders;
  note?: string;
  createdBy: string;
  createdAt: string;
}

// ─── Payments ─────────────────────────────────────────────────────────────────

export interface OrderPayment {
  id: string;
  orderId: string;
  amount: string;
  currency: string;
  status: string;
  paymentMethod: string;
  transactionId?: string;
  createdAt: string;
}

export interface CapturePaymentRequest {
  finalAmount: number;
  reason?: string;
}

export interface PaymentAction {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  orderId: string;
  action: string;
  paymentMethod: string;
  gateway: string;
  status: string;
  amount: string;
  currency: string;
  metadata: {
    ipAddress?: string;
    paypalOrderId?: string;
    paypalStatus?: string;
    gatewayTransactionId?: string;
    authorizationId?: string;
    authorizedAt?: string;
    captureId?: string;
    capturedAt?: string;
    capturedAmount?: number;
    originalAuthorizedAmount?: number;
    payer?: {
      name?: { given_name?: string; surname?: string };
      email_address?: string;
      payer_id?: string;
      address?: { country_code?: string };
    };
  } | null;
  performedById: string | null;
  performedBy: string | null;
}

export interface CaptureDeadline {
  isManualCapture: boolean;
  daysRemaining: number;
}

// ─── Returns ──────────────────────────────────────────────────────────────────

export interface OrderReturn {
  id: string;
  orderId: string;
  reason: string;
  status: string;
  requestedAt: string;
  resolvedAt?: string;
}

export interface ReturnRequestBody {
  failureSymptom: string;
  wasPartInstalled: boolean;
  machineBrandAndRef?: string;
  workingHours?: string;
  whatHappened?: string;
  whenHappened?: string;
  howHappened?: string;
  machineDoingWhat?: string;
  howDetected?: string;
  specialComments?: string;
  attachedPictures?: string[];
  items?: { orderItemId: string; quantity?: number }[];
}

export interface ReturnRequestDetail {
  id: string;
  createdAt: string;
  updatedAt: string;
  rmaNumber: string;
  orderId: string;
  customerId: string;
  status: string;
  reason: string | null;
  refundAmount: string;
  restockingFee: string;
  refundMethod: string | null;
  completedAt: string | null;
  creditMemoId: string | null;
  items: { orderItemId: string; quantity: number }[] | null;
  metadata: {
    source: string;
    failureSymptom: string | null;
    wasPartInstalled: boolean | null;
    machineBrandAndRef: string | null;
  } | null;
}

// ─── Commission Data ──────────────────────────────────────────────────────────

export interface TrackingRow {
  carrier: string;
  trackingNumber: string;
  amount: string;
  billingData: string;
}

export interface OrderCommissionData {
  salespersonId?: string;
  revenue: string;
  refunded: string;
  cost: string;
  shippingCost: string;
  supplierRefund: string;
  transactionFee: string;
  supplierSalesTax: string;
  commissionRate: string;
  manualCommissionAmount?: string;
  grossProfit: string;
  commissionAmount: string;
  brand?: string;
  paymentMethod?: string;
  accountingMonth?: string;
  note?: string;
  payFromCustomerFileUrl?: string;
  payToSupplierFileUrl?: string;
  shippingFileUrl?: string;
  okSalesTaxFileUrl?: string;
  customerRefundFileUrl?: string;
  supplierRefundFileUrl?: string;
  trackingData?: TrackingRow[];
  inboundTracking?: string;
  localTracking?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface UpdateOrderCommissionRequest {
  salespersonId?: string;
  revenue?: string;
  refunded?: string;
  cost?: string;
  shippingCost?: string;
  supplierRefund?: string;
  transactionFee?: string;
  supplierSalesTax?: string;
  commissionRate?: string;
  manualCommissionAmount?: string;
  brand?: string;
  paymentMethod?: string;
  accountingMonth?: string;
  note?: string;
  trackingData?: string;
  inboundTracking?: string;
  localTracking?: string;
}

// ─── Void ─────────────────────────────────────────────────────────────────────

export type VoidReason = 'duplicate' | 'fraudulent' | 'request_by_customer';

export interface VoidOrderRequest {
  reason?: string;
}

// ─── Manual payment confirmation ──────────────────────────────────────────────

export const MANUAL_PAYMENT_METHODS = [
  'check',
  'zelle',
  'ach',
  'wire',
  'bank_transfer',
  'credit_card',
  'cash',
  'other',
] as const;

export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

export const PAYMENT_PROOF_FILE_CATEGORY = 'payment_proof';

export interface ConfirmManualPaymentRequest {
  method: ManualPaymentMethod;
  amount: number;
  paidAt?: string;
  reference?: string;
  note?: string;
  proofFileId?: string;
}

// ─── ETA ──────────────────────────────────────────────────────────────────────

export interface EtaRequest {
  eta: string;
}

export interface OrderEta {
  orderId: string;
  eta: string | null;
}

// ─── Add / Edit Item ──────────────────────────────────────────────────────────

export interface AddOrderItemRequest {
  quantity: number;
  unitPrice: string;
  /** Set only when the part was picked from inventory search */
  productId?: string;
  partNumber: string;
  productName: string;
  brandName?: string;
  condition?: string;
}

/** One "Edit order" session — only changed values need to be sent */
export interface BulkUpdateOrderItemsRequest {
  items?: { itemId: string; quantity?: number; unitPrice?: string }[];
  removeItemIds?: string[];
  shippingAmount?: string;
  handlingAmount?: string;
  insuranceAmount?: string;
  feesAmount?: string;
  /** A changed value is kept as a manual tax amount */
  taxAmount?: string;
}

export interface SetTaxExemptionRequest {
  isTaxExempt: boolean;
  note?: string;
}

export interface UpdateOrderItemRequest {
  quantity?: number;
  unitPrice?: string;
  shippingAmount?: string;
  handlingAmount?: string;
  insuranceAmount?: string;
  feesAmount?: string;
  /** A changed value is kept as a manual tax amount (not recalculated) */
  taxAmount?: string;
}

// ─── Review ───────────────────────────────────────────────────────────────────

export interface OrderReview {
  id: string;
  review: string;
  reviewerName: string;
  createdAt: string;
}

// ─── Flag ─────────────────────────────────────────────────────────────────────

export interface FlagOrderRequest {
  flag: 'red' | 'orange' | 'green';
}

// ─── Additional Invoice (child orders) ────────────────────────────────────────

export type AdditionalInvoiceType =
  | 'additional_cost'
  | 'additional_shipping_cost'
  | 'florida_sales_tax'
  | 'additional_order';

export const ADDITIONAL_INVOICE_TYPE_LABELS: Record<AdditionalInvoiceType, string> = {
  additional_cost: 'Additional cost',
  additional_shipping_cost: 'Additional shipping cost',
  florida_sales_tax: 'Florida sales tax',
  additional_order: 'Additional Order',
};

export interface CreateAdditionalInvoiceRequest {
  reason: string;
  shippingAmount: string;
  sendEmail: boolean;
  message?: string;
}

// ─── Order Vendors ────────────────────────────────────────────────────────────

export interface OrderVendor {
  id: string;
  vendorId: string;
  vendorName: string;
  items: { orderItemId: string; quantity: number }[];
}

// ─── Status Logs ──────────────────────────────────────────────────────────────

export interface OrderStatusLog {
  id: string;
  orderId: string;
  performedById: string | null;
  performedBy: string | null;
  source: string | null;
  statusLabel: string;
  amount: string | null;
  currency: string | null;
  paymentAction: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrderEmailInfo {
  id: string;
  date: string;
  type: string;
  sender: string;
  recipient: string;
  status: string;
  errorMessage?: string;
}

// ─── Fraud Review ─────────────────────────────────────────────────────────────

export interface FraudReviewActionDto {
  orderId: string;
  action: 'approve' | 'reject' | 'request_info' | 'cancel';
  reviewNotes?: string;
  additionalInfoRequested?: string[];
}

export const adminOrdersApi = {
  getOrders: async (
    params?: OrderParams
  ): Promise<PagedListResponse<Order>> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS, { params });
    return response.data;
  },

  getOrderById: async (id: string): Promise<Order> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_BY_ID(id));
    return response.data;
  },

  assignSalesperson: async (
    id: string,
    data: AssignSalespersonRequest
  ): Promise<Order> => {
    const response = await axiosClient.patch(
      API_ENDPOINTS.ORDERS_ASSIGN_SALESPERSON(id),
      data
    );
    return response.data;
  },

  reportItemsAvailability: async (
    id: string,
    data: ItemsAvailabilityRequest
  ): Promise<Order> => {
    const response = await axiosClient.patch(
      API_ENDPOINTS.ORDERS_ITEMS_AVAILABILITY(id),
      data
    );
    return response.data;
  },

  updateOrderStatus: async (id: string, data: UpdateOrderStatusRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_UPDATE(id), data);
    return response.data;
  },

  updateOrder: async (id: string, data: UpdateOrderRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_UPDATE(id), data);
    return response.data;
  },

  updateShippingAddress: async (id: string, data: UpdateShippingAddressRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_SHIPPING_ADDRESS(id), data);
    return response.data;
  },

  cancelOrder: async (id: string, data: CancelOrderRequest): Promise<Order> => {
    const response = await axiosClient.patch(
      API_ENDPOINTS.ORDERS_CANCEL(id),
      data
    );
    return response.data;
  },

  approvePartial: async (
    id: string,
    data: ApprovePartialRequest
  ): Promise<Order> => {
    const response = await axiosClient.patch(
      API_ENDPOINTS.ORDERS_APPROVE_PARTIAL(id),
      data
    );
    return response.data;
  },

  refundOrder: async (id: string, data: RefundOrderRequest): Promise<Order> => {
    const response = await axiosClient.post(
      API_ENDPOINTS.PAYMENTS_REFUND(id),
      data
    );
    return response.data;
  },

  downloadInvoice: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_INVOICE(id), {
      responseType: 'blob',
    });
    return response.data;
  },

  downloadInvoiceNoShipping: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_INVOICE(id), {
      responseType: 'blob',
      params: { excludeShipping: true },
    });
    return response.data;
  },

  downloadCCAuthForm: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(
      API_ENDPOINTS.ORDERS_CC_AUTH_FORM(id),
      { responseType: 'blob' }
    );
    return response.data;
  },

  downloadStatusConfirmation: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(
      API_ENDPOINTS.ORDERS_STATUS_CONFIRMATION(id),
      { responseType: 'blob' }
    );
    return response.data;
  },

  sendDocument: async (id: string, data: SendDocumentRequest): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.ORDERS_SEND_DOCUMENT(id), data);
  },

  getOrdersByCustomer: async (customerId: string): Promise<Order[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_BY_CUSTOMER(customerId));
    return response.data;
  },

  // ─── Order relations ────────────────────────────────────────────────────────

  getChildOrders: async (id: string): Promise<Order[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_CHILDREN(id));
    return response.data;
  },

  getGroupOrder: async (id: string): Promise<OrderGroup> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_GROUP(id));
    return response.data;
  },

  getParentOrder: async (id: string): Promise<Order> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_PARENT(id));
    return response.data;
  },

  getOrderReport: async (id: string): Promise<Blob> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_REPORT(id), { responseType: 'blob' });
    return response.data;
  },

  getDoubledOrder: async (id: string): Promise<Order> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_DOUBLED(id));
    return response.data;
  },

  updateOrderFlag: async (id: string, data: FlagOrderRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_FLAG(id), data);
    return response.data;
  },

  voidOrder: async (id: string, data: VoidOrderRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_VOID(id), data);
    return response.data;
  },

  approveOrderEta: async (id: string, data: EtaRequest): Promise<OrderEta> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_ETA(id), data);
    return response.data;
  },

  addOrderItem: async (id: string, data: AddOrderItemRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_ADD_ITEM(id), data);
    return response.data;
  },

  updateOrderItem: async (id: string, itemId: string, data: UpdateOrderItemRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_UPDATE_ITEM(id, itemId), data);
    return response.data;
  },

  bulkUpdateOrderItems: async (id: string, data: BulkUpdateOrderItemsRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_UPDATE_ITEMS(id), data);
    return response.data;
  },

  setTaxExemption: async (id: string, data: SetTaxExemptionRequest): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_TAX_EXEMPTION(id), data);
    return response.data;
  },

  deleteOrderItem: async (id: string, itemId: string): Promise<Order> => {
    const response = await axiosClient.delete(API_ENDPOINTS.ORDERS_DELETE_ITEM(id, itemId));
    return response.data;
  },

  // ─── Order files ────────────────────────────────────────────────────────────

  getOrderFiles: async (id: string): Promise<OrderFile[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_FILES(id));
    return response.data;
  },

  uploadOrderFile: async (id: string, file: File, category?: string): Promise<OrderFile> => {
    const form = new FormData();
    form.append('file', file);
    if (category) form.append('category', category);
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_FILES(id), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteOrderFile: async (id: string, fileId: string): Promise<void> => {
    await axiosClient.delete(API_ENDPOINTS.ORDERS_FILE_BY_ID(id, fileId));
  },

  // ─── Salesperson tab ────────────────────────────────────────────────────────

  uploadSalespersonAttachment: async (id: string, file: File): Promise<OrderFile> => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_SALESPERSON_FILES(id), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // ─── Notes ──────────────────────────────────────────────────────────────────

  getOrderNotes: async (id: string): Promise<OrderNote[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_NOTES(id));
    return response.data;
  },

  addOrderNote: async (id: string, data: AddOrderNoteRequest): Promise<OrderNote> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_NOTES(id), data);
    return response.data;
  },

  sendFeedbackRequest: async (id: string): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.ORDERS_FEEDBACK_REQUEST(id), {});
  },

  sendPartNotAvailable: async (id: string): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.ORDERS_PART_NOT_AVAILABLE(id), {});
  },

  sendPaymentRequest: async (id: string): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.ORDERS_PAYMENT_REQUEST(id), {});
  },

  // ─── Status logs ────────────────────────────────────────────────────────────

  getStatusLogs: async (id: string): Promise<StatusLog[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_STATUS_LOGS(id));
    return response.data;
  },

  getStatusLogById: async (id: string, logId: string): Promise<StatusLog> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_STATUS_LOG_BY_ID(id, logId));
    return response.data;
  },

  // ─── Payments ───────────────────────────────────────────────────────────────

  getOrderPayments: async (id: string): Promise<OrderPayment[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_PAYMENTS(id));
    return response.data;
  },

  capturePayment: async (orderId: string, data: CapturePaymentRequest): Promise<OrderPayment> => {
    const response = await axiosClient.post(API_ENDPOINTS.PAYMENTS_CAPTURE(orderId), data);
    return response.data;
  },

  confirmManualPayment: async (orderId: string, data: ConfirmManualPaymentRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_CONFIRM_PAYMENT(orderId), data);
    return response.data;
  },

  /** POST /orders/:id/files responds with the whole updated order, not the file. */
  uploadPaymentProof: async (orderId: string, file: File): Promise<Order> => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', PAYMENT_PROOF_FILE_CATEGORY);
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_FILES(orderId), form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getPaymentActions: async (id: string): Promise<PaymentAction[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_PAYMENT_ACTIONS(id));
    return response.data;
  },

  getCaptureDeadline: async (id: string): Promise<CaptureDeadline> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_CAPTURE_DEADLINE(id));
    return response.data;
  },

  // ─── Returns ────────────────────────────────────────────────────────────────

  getOrderReturns: async (id: string): Promise<OrderReturn[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_RETURNS(id));
    return response.data;
  },

  getReturnRequest: async (id: string): Promise<ReturnRequestDetail | null> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_RETURNS(id));
    const data: ReturnRequestDetail[] = response.data;
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  },

  submitReturnRequest: async (id: string, data: ReturnRequestBody): Promise<OrderReturn> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_RETURN_REQUEST(id), data);
    return response.data;
  },

  // ─── Vendors ────────────────────────────────────────────────────────────────

  getOrderVendors: async (id: string): Promise<OrderVendor[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_VENDORS(id));
    return response.data;
  },

  // ─── Additional Invoice ──────────────────────────────────────────────────────

  createAdditionalInvoice: async (id: string, data: CreateAdditionalInvoiceRequest): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_CHILD_ORDERS(id), data);
    return response.data;
  },
  // ─── Fraud Review ───────────────────────────────────────────────────────────

  processFraudAction: async (data: FraudReviewActionDto): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.FRAUD_REVIEW_ACTION, data);
    return response.data;
  },

  // ─── Status Logs ────────────────────────────────────────────────────────────

  getOrderStatusLogs: async (id: string): Promise<OrderStatusLog[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_STATUS_LOGS(id));
    return response.data;
  },

  getOrderEmailInfo: async (id: string): Promise<OrderEmailInfo[]> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_EMAIL_INFO(id));
    return response.data;
  },

  getShareableLink: async (id: string): Promise<{ url: string }> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_SHAREABLE_LINK(id));
    return response.data;
  },

  getOrderCommissionData: async (id: string): Promise<OrderCommissionData | null> => {
    const response = await axiosClient.get(API_ENDPOINTS.ORDERS_COMMISSION_DATA(id));
    return response.data;
  },

  updateOrderCommissionData: async (
    id: string,
    data: UpdateOrderCommissionRequest,
    files?: Partial<Record<'payFromCustomerFile' | 'payToSupplierFile' | 'shippingFile' | 'okSalesTaxFile' | 'customerRefundFile' | 'supplierRefundFile', File>>
  ): Promise<OrderCommissionData> => {
    const formData = new FormData();
    (Object.keys(data) as (keyof UpdateOrderCommissionRequest)[]).forEach((key) => {
      const val = data[key];
      if (val !== undefined) formData.append(key, val);
    });
    if (files) {
      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });
    }
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_COMMISSION_DATA(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  addOrderReview: async (id: string, review: string): Promise<Order> => {
    const response = await axiosClient.post(API_ENDPOINTS.ORDERS_REVIEWS(id), { review });
    return response.data;
  },

  editOrderReview: async (id: string, reviewId: string, review: string): Promise<Order> => {
    const response = await axiosClient.patch(API_ENDPOINTS.ORDERS_REVIEW_BY_ID(id, reviewId), { review });
    return response.data;
  },

  deleteOrderReview: async (id: string, reviewId: string): Promise<Order> => {
    const response = await axiosClient.delete(API_ENDPOINTS.ORDERS_REVIEW_BY_ID(id, reviewId));
    return response.data;
  },
};
